import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Header } from "@/components/Header";
import { ManualeButton } from "@/components/ManualeButton";
import { FileUpload } from "@/components/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { ClipboardCheck, Plus, Pencil, Trash2, ArrowLeft, GraduationCap, CalendarClock, CheckCircle2, GitMerge, FileSpreadsheet, ScrollText, PenLine } from "lucide-react";

const thisYear = () => new Date().getFullYear();

export default function Compliance() {
  const navigate = useNavigate();
  const [persone, setPersone] = useState([]);
  useEffect(() => { api.get("/compliance/persone").then((r) => setPersone(r.data)).catch(() => setPersone([])); }, []);
  const nomeOf = (id) => persone.find((p) => p.id === id)?.nome || "—";

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <button data-testid="comp-back-btn" onClick={() => navigate("/gestione-agenzia")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft className="h-4 w-4" /> Gestione Agenzia</button>
        <div className="flex items-start justify-between gap-3 mb-8 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center"><ClipboardCheck className="h-6 w-6" /></div>
            <div>
              <h1 className="font-heading text-3xl font-medium text-slate-900">Compliance Agenzia</h1>
              <p className="text-slate-500 text-sm">Aggiornamento professionale IVASS e scadenzario adempimenti.</p>
            </div>
          </div>
          <ManualeButton route="/manuale/compliance" />
        </div>

        <Tabs defaultValue="formazione">
          <TabsList data-testid="comp-tabs">
            <TabsTrigger value="formazione" data-testid="tab-formazione"><GraduationCap className="h-4 w-4 mr-1.5" />Formazione IVASS</TabsTrigger>
            <TabsTrigger value="scadenze" data-testid="tab-scadenze"><CalendarClock className="h-4 w-4 mr-1.5" />Scadenze</TabsTrigger>
          </TabsList>
          <TabsContent value="formazione" className="mt-6"><FormazioneTab persone={persone} nomeOf={nomeOf} /></TabsContent>
          <TabsContent value="scadenze" className="mt-6"><ScadenzeTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function FormazioneTab({ persone, nomeOf }) {
  const [anno, setAnno] = useState(thisYear());
  const [riepilogo, setRiepilogo] = useState(null);
  const [corsi, setCorsi] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [merge, setMerge] = useState(null); // { src, target_id }
  const [filtroPersona, setFiltroPersona] = useState(""); // "" = tutti
  const load = () => {
    api.get("/compliance/formazione/riepilogo", { params: { anno } }).then((r) => setRiepilogo(r.data)).catch(() => setRiepilogo(null));
    api.get("/compliance/corsi", { params: { anno } }).then((r) => setCorsi(r.data)).catch(() => setCorsi([]));
  };
  useEffect(() => { load(); }, [anno]);
  const corsiVisibili = filtroPersona ? corsi.filter((c) => c.persona_id === filtroPersona) : corsi;
  const openExport = (path) => window.open(`${API}${path}`, "_blank");
  const exportXlsx = () => openExport(`/compliance/formazione/export/xlsx?anno=${anno}${filtroPersona ? `&persona_id=${filtroPersona}` : ""}`);
  const openAutocert = (pid) => openExport(`/compliance/formazione/autocertificazione/pdf?anno=${anno}&persona_id=${pid}`);
  const exportAutocertAll = () => openExport(`/compliance/formazione/autocertificazioni/pdf?anno=${anno}`);
  const [anag, setAnag] = useState(null); // { persona_id, nome, codice_fiscale, luogo_nascita, data_nascita, rui_sezione, rui_number }
  const [otpDlg, setOtpDlg] = useState(null); // { persona_id, nome, dest, code, sending, signing }
  const saveAnag = async () => {
    try {
      await api.put(`/compliance/persone/${anag.persona_id}/anagrafica`, {
        codice_fiscale: anag.codice_fiscale || "", luogo_nascita: anag.luogo_nascita || "",
        data_nascita: anag.data_nascita || "", rui_sezione: anag.rui_sezione || "", rui_number: anag.rui_number || "",
      });
      toast.success("Dati anagrafici salvati"); setAnag(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); }
  };
  const sendAutocertOtp = async () => {
    setOtpDlg((d) => ({ ...d, sending: true }));
    try {
      const { data } = await api.post(`/compliance/formazione/autocert/${otpDlg.persona_id}/send-otp?anno=${anno}`);
      setOtpDlg((d) => ({ ...d, dest: data.destinazione, sending: false }));
      toast.success(`OTP inviato via SMS a ${data.destinazione}`);
    } catch (e) { setOtpDlg((d) => ({ ...d, sending: false })); toast.error(e.response?.data?.detail || "Invio OTP non riuscito"); }
  };
  const signAutocert = async () => {
    setOtpDlg((d) => ({ ...d, signing: true }));
    try {
      await api.post(`/compliance/formazione/autocert/${otpDlg.persona_id}/sign?anno=${anno}`, { otp: otpDlg.code });
      toast.success("Autocertificazione firmata"); setOtpDlg(null); load();
    } catch (e) { setOtpDlg((d) => ({ ...d, signing: false })); toast.error(e.response?.data?.detail || "Firma non riuscita"); }
  };
  const empty = { persona_id: "", titolo: "", ente: "", ore: 0, data: new Date().toISOString().slice(0, 10), modalita: "aula", note: "" };
  const save = async () => {
    if (!dlg.persona_id || !dlg.titolo?.trim()) return toast.error("Persona e titolo obbligatori");
    try {
      if (dlg.id) await api.put(`/compliance/corsi/${dlg.id}`, dlg);
      else await api.post("/compliance/corsi", dlg);
      toast.success("Salvato"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const del = async (r) => {
    if (!window.confirm(`Eliminare il corso «${r.titolo}»? L'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/compliance/corsi/${r.id}`);
      toast.success("Corso eliminato"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Eliminazione non riuscita"); }
  };
  const delPersona = async (r) => {
    if (!window.confirm(`Eliminare definitivamente «${r.nome}» dall'anagrafica? (nessuna ora di formazione registrata)`)) return;
    try {
      await api.delete(`/compliance/persone/${r.persona_id}`);
      toast.success(`«${r.nome}» eliminato`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Eliminazione non riuscita"); }
  };
  const doMerge = async () => {
    if (!merge?.target_id) return toast.error("Seleziona il nominativo da mantenere");
    try {
      const { data } = await api.post(`/compliance/persone/${merge.src.persona_id}/merge`, { target_id: merge.target_id });
      const n = Object.values(data.spostati || {}).reduce((a, b) => a + b, 0);
      toast.success(`«${data.sorgente}» unito a «${data.destinazione}» (${n} elementi spostati)`);
      setMerge(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Unione non riuscita"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2"><Label className="text-sm">Anno</Label><Input type="number" value={anno} onChange={(e) => setAnno(Number(e.target.value))} className="w-28" data-testid="form-anno" /></div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Collaboratore</Label>
            <Select value={filtroPersona || "all"} onValueChange={(v) => setFiltroPersona(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56" data-testid="form-filtro-persona"><SelectValue placeholder="Tutti" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="form-filtro-persona-all">Tutti i collaboratori</SelectItem>
                {persone.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
              </SelectContent>
            </Select>
            {filtroPersona && <button onClick={() => setFiltroPersona("")} className="text-xs text-slate-400 hover:text-slate-700 underline" data-testid="form-filtro-reset">azzera</button>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="form-export-xlsx" onClick={exportXlsx}><FileSpreadsheet className="h-4 w-4 mr-1.5" />Esporta Excel</Button>
          <Button variant="outline" data-testid="form-autocert-all" onClick={exportAutocertAll}><ScrollText className="h-4 w-4 mr-1.5" />Autocert. tutti</Button>
          <Button data-testid="corso-add" onClick={() => setDlg({ ...empty })}><Plus className="h-4 w-4 mr-1.5" />Nuovo corso</Button>
        </div>
      </div>

      {/* Riepilogo ore per intermediario */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="form-riepilogo">
        {riepilogo?.righe?.map((r) => (
          <div key={r.persona_id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-slate-800">{r.nome} <span className="text-xs text-slate-400">· {r.ruolo}</span></div>
              <div className="flex items-center gap-2">
                {r.autocert_valida
                  ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid={`comp-autocert-firmata-${r.persona_id}`}><CheckCircle2 className="h-3.5 w-3.5" />Autocert. firmata</span>
                  : (r.completato ? <span className="inline-flex items-center gap-1 text-xs text-green-600"><CheckCircle2 className="h-3.5 w-3.5" />Obbligo assolto</span>
                    : <span className="text-xs text-orange-600">Mancano {r.deficit}h</span>)}
                <button onClick={() => setAnag({ persona_id: r.persona_id, nome: r.nome, codice_fiscale: r.codice_fiscale || "", luogo_nascita: r.luogo_nascita || "", data_nascita: r.data_nascita || "", rui_sezione: r.rui_sezione || "", rui_number: r.rui_number || "" })} title="Dati anagrafici per l'autocertificazione" className="text-slate-300 hover:text-brand-blue transition-colors" data-testid={`comp-anag-${r.persona_id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => openAutocert(r.persona_id)} title="Scarica l'autocertificazione PDF" className="text-slate-300 hover:text-brand-blue transition-colors" data-testid={`comp-autocert-${r.persona_id}`}>
                  <ScrollText className="h-4 w-4" />
                </button>
                <button onClick={() => setOtpDlg({ persona_id: r.persona_id, nome: r.nome, code: "" })} title="Firma l'autocertificazione con OTP via SMS" className="text-slate-300 hover:text-emerald-600 transition-colors" data-testid={`comp-autocert-otp-${r.persona_id}`}>
                  <PenLine className="h-4 w-4" />
                </button>
                {riepilogo?.is_admin && (
                  <button onClick={() => setMerge({ src: r, target_id: "" })} title="Unisci questo nominativo a un altro (senza perdere nulla)" className="text-slate-300 hover:text-brand-blue transition-colors" data-testid={`comp-merge-persona-${r.persona_id}`}>
                    <GitMerge className="h-4 w-4" />
                  </button>
                )}
                {r.eliminabile && (
                  <button onClick={() => delPersona(r)} title="Elimina persona duplicata (senza ore di formazione)" className="text-slate-300 hover:text-red-600 transition-colors" data-testid={`comp-del-persona-${r.persona_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <Progress value={Math.min(100, (r.ore / r.obbligo) * 100)} className="h-2" />
            <div className="text-xs text-slate-500 mt-1">{r.ore}h / {r.obbligo}h</div>
          </div>
        ))}
        {(!riepilogo || riepilogo.righe.length === 0) && <div className="text-slate-400 text-sm">Nessuna persona attiva.</div>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader><TableRow><TableHead className="sticky left-0 bg-white z-20 border-r border-slate-100">Azioni</TableHead><TableHead>Persona</TableHead><TableHead>Corso</TableHead><TableHead>Ente</TableHead><TableHead>Ore</TableHead><TableHead>Data</TableHead><TableHead>Attestato</TableHead></TableRow></TableHeader>
          <TableBody>
            {corsiVisibili.map((r) => (
              <TableRow key={r.id} data-testid={`corso-row-${r.id}`}>
                <TableCell className="sticky left-0 bg-white z-10 border-r border-slate-100 whitespace-nowrap align-top">
                  <div className="flex flex-col gap-1.5">
                    <button onClick={() => setDlg({ ...r })} data-testid={`corso-edit-${r.id}`} className="inline-flex items-center justify-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-brand-blue hover:text-brand-blue transition-colors"><Pencil className="h-3.5 w-3.5" /> Correggi</button>
                    <button onClick={() => del(r)} data-testid={`corso-del-${r.id}`} className="inline-flex items-center justify-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50 transition-colors"><Trash2 className="h-3.5 w-3.5" /> Elimina</button>
                  </div>
                </TableCell>
                <TableCell className="font-medium">{nomeOf(r.persona_id)}</TableCell>
                <TableCell>{r.titolo}</TableCell>
                <TableCell>{r.ente || "—"}</TableCell>
                <TableCell>{r.ore}</TableCell>
                <TableCell>{r.data}</TableCell>
                <TableCell className="w-48"><FileUpload modulo="attestato" refId={r.id} compact testid={`att-${r.id}`} accept=".pdf,.png,.jpg,.jpeg" /></TableCell>
              </TableRow>
            ))}
            {corsiVisibili.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">{filtroPersona ? `Nessun corso per ${nomeOf(filtroPersona)} nel ${anno}.` : `Nessun corso registrato per ${anno}.`}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="corso-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuovo"} corso di aggiornamento</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label>Persona</Label><Select value={dlg.persona_id} onValueChange={(v) => setDlg({ ...dlg, persona_id: v })}><SelectTrigger data-testid="corso-persona"><SelectValue placeholder="Seleziona" /></SelectTrigger><SelectContent>{persone.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Titolo corso</Label><Input value={dlg.titolo} onChange={(e) => setDlg({ ...dlg, titolo: e.target.value })} data-testid="corso-titolo" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ente formatore</Label><Input value={dlg.ente} onChange={(e) => setDlg({ ...dlg, ente: e.target.value })} /></div>
                <div><Label>Ore</Label><Input type="number" value={dlg.ore} onChange={(e) => setDlg({ ...dlg, ore: e.target.value })} data-testid="corso-ore" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data</Label><Input type="date" value={dlg.data} onChange={(e) => setDlg({ ...dlg, data: e.target.value })} /></div>
                <div><Label>Modalità</Label><Select value={dlg.modalita} onValueChange={(v) => setDlg({ ...dlg, modalita: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="aula">Aula</SelectItem><SelectItem value="e-learning">E-learning</SelectItem><SelectItem value="webinar">Webinar</SelectItem></SelectContent></Select></div>
              </div>
              <div><Label>Note</Label><Textarea value={dlg.note} onChange={(e) => setDlg({ ...dlg, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="corso-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!merge} onOpenChange={(o) => !o && setMerge(null)}>
        <DialogContent data-testid="comp-merge-dialog">
          <DialogHeader><DialogTitle>Unisci nominativo duplicato</DialogTitle></DialogHeader>
          {merge && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Stai unendo <strong>{merge.src.nome}</strong> <span className="text-slate-400">· {merge.src.ruolo}</span> in un altro nominativo.
                Corsi di formazione, calendario, ferie, stipendi ed eventuale account verranno spostati sul nominativo scelto,
                poi <strong>{merge.src.nome}</strong> verrà eliminato. Nessun dato viene perso.
              </p>
              <div>
                <Label>Mantieni questo nominativo (destinazione)</Label>
                <Select value={merge.target_id} onValueChange={(v) => setMerge({ ...merge, target_id: v })}>
                  <SelectTrigger data-testid="comp-merge-target"><SelectValue placeholder="Seleziona il nominativo da mantenere" /></SelectTrigger>
                  <SelectContent>
                    {(riepilogo?.righe || []).filter((x) => x.persona_id !== merge.src.persona_id).map((x) => (
                      <SelectItem key={x.persona_id} value={x.persona_id}>{x.nome} · {x.ruolo} ({x.ore}h)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMerge(null)}>Annulla</Button>
            <Button onClick={doMerge} data-testid="comp-merge-confirm">Unisci ed elimina duplicato</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!anag} onOpenChange={(o) => !o && setAnag(null)}>
        <DialogContent data-testid="comp-anag-dialog">
          <DialogHeader><DialogTitle>Dati anagrafici — {anag?.nome}</DialogTitle></DialogHeader>
          {anag && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Compilando questi dati l'autocertificazione uscirà già pronta, senza righe da riempire a mano.</p>
              <div><Label>Codice fiscale</Label><Input value={anag.codice_fiscale} onChange={(e) => setAnag({ ...anag, codice_fiscale: e.target.value.toUpperCase() })} data-testid="anag-cf" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Luogo di nascita</Label><Input value={anag.luogo_nascita} onChange={(e) => setAnag({ ...anag, luogo_nascita: e.target.value })} data-testid="anag-luogo" /></div>
                <div><Label>Data di nascita</Label><Input type="date" value={anag.data_nascita} onChange={(e) => setAnag({ ...anag, data_nascita: e.target.value })} data-testid="anag-data" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Sezione RUI</Label>
                  <Select value={anag.rui_sezione || "none"} onValueChange={(v) => setAnag({ ...anag, rui_sezione: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="anag-sezione"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      <SelectItem value="A">A</SelectItem><SelectItem value="B">B</SelectItem><SelectItem value="E">E</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>N. iscrizione RUI</Label><Input value={anag.rui_number} onChange={(e) => setAnag({ ...anag, rui_number: e.target.value })} data-testid="anag-rui" /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setAnag(null)}>Annulla</Button><Button onClick={saveAnag} data-testid="anag-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!otpDlg} onOpenChange={(o) => !o && setOtpDlg(null)}>
        <DialogContent data-testid="comp-autocert-otp-dialog">
          <DialogHeader><DialogTitle>Firma autocertificazione — {otpDlg?.nome}</DialogTitle></DialogHeader>
          {otpDlg && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Invia un codice OTP via SMS al cellulare del collaboratore (dall'anagrafica). Il collaboratore detta il codice e lo inserisci qui per firmare l'autocertificazione dell'anno {anno}.</p>
              <Button variant="outline" size="sm" disabled={otpDlg.sending} onClick={sendAutocertOtp} data-testid="autocert-send-otp">{otpDlg.sending ? "Invio…" : (otpDlg.dest ? `Reinvia SMS a ${otpDlg.dest}` : "Invia OTP via SMS")}</Button>
              {otpDlg.dest && <p className="text-xs text-emerald-600">Codice inviato a {otpDlg.dest}.</p>}
              <Input value={otpDlg.code} onChange={(e) => setOtpDlg({ ...otpDlg, code: e.target.value.replace(/\D/g, "") })} inputMode="numeric" placeholder="Codice OTP a 6 cifre" maxLength={6} data-testid="autocert-otp-input" />
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setOtpDlg(null)}>Annulla</Button><Button disabled={!otpDlg?.dest || (otpDlg?.code || "").length < 6 || otpDlg?.signing} onClick={signAutocert} data-testid="autocert-sign-confirm">Firma</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScadenzeTab() {
  const [rows, setRows] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get("/compliance/scadenze").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const empty = { titolo: "", tipo: "adempimento", data_scadenza: new Date().toISOString().slice(0, 10), responsabile: "", ricorrenza: "una_tantum", stato: "aperta", note: "" };
  const save = async () => {
    if (!dlg.titolo?.trim()) return toast.error("Titolo obbligatorio");
    try {
      if (dlg.id) await api.put(`/compliance/scadenze/${dlg.id}`, dlg);
      else await api.post("/compliance/scadenze", dlg);
      toast.success("Salvato"); setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const toggle = async (r) => { await api.put(`/compliance/scadenze/${r.id}`, { ...r, stato: r.stato === "completata" ? "aperta" : "completata" }); load(); };
  const del = async (r) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/compliance/scadenze/${r.id}`); load(); };

  return (
    <div>
      <div className="flex justify-end mb-4"><Button data-testid="scad-add" onClick={() => setDlg({ ...empty })}><Plus className="h-4 w-4 mr-1.5" />Nuova scadenza</Button></div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead></TableHead><TableHead>Adempimento</TableHead><TableHead>Scadenza</TableHead><TableHead>Ricorrenza</TableHead><TableHead>Responsabile</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`scad-row-${r.id}`} className={r.stato === "completata" ? "opacity-50" : ""}>
                <TableCell><input type="checkbox" checked={r.stato === "completata"} onChange={() => toggle(r)} data-testid={`scad-toggle-${r.id}`} /></TableCell>
                <TableCell className="font-medium">{r.titolo}</TableCell>
                <TableCell>{r.data_scadenza}</TableCell>
                <TableCell className="capitalize">{r.ricorrenza}</TableCell>
                <TableCell>{r.responsabile || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => setDlg({ ...r })} className="text-slate-400 hover:text-brand-blue mr-3"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Nessuna scadenza registrata.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="scad-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Modifica" : "Nuova"} scadenza</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label>Adempimento</Label><Input value={dlg.titolo} onChange={(e) => setDlg({ ...dlg, titolo: e.target.value })} data-testid="scad-titolo" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data scadenza</Label><Input type="date" value={dlg.data_scadenza} onChange={(e) => setDlg({ ...dlg, data_scadenza: e.target.value })} data-testid="scad-data" /></div>
                <div><Label>Ricorrenza</Label><Select value={dlg.ricorrenza} onValueChange={(v) => setDlg({ ...dlg, ricorrenza: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="una_tantum">Una tantum</SelectItem><SelectItem value="settimanale">Settimanale</SelectItem><SelectItem value="mensile">Mensile</SelectItem><SelectItem value="trimestrale">Trimestrale</SelectItem><SelectItem value="annuale">Annuale</SelectItem></SelectContent></Select></div>
              </div>
              <div><Label>Responsabile</Label><Input value={dlg.responsabile} onChange={(e) => setDlg({ ...dlg, responsabile: e.target.value })} /></div>
              <div><Label>Note</Label><Textarea value={dlg.note} onChange={(e) => setDlg({ ...dlg, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDlg(null)}>Annulla</Button><Button onClick={save} data-testid="scad-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
